import { Box, Button, Fade, Flex, Form, Grid, Text, TextField } from '@approvalmax/ui/src/components';
import { validatorHelpers } from '@approvalmax/utils';
import { useAtomValue } from 'jotai';
import { memo, useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { XeroReconnectWarning } from '../XeroReconnectWarning/XeroReconnectWarning';
import { xeroReconnectWarningVisibleState } from '../XeroReconnectWarning/XeroReconnectWarning.states';
import { transform } from './NewXeroBankAccountForm.helpers';
import { messages } from './NewXeroBankAccountForm.messages';
import { FormState, NewXeroBankAccountFormProps } from './NewXeroBankAccountForm.types';

export const NewXeroBankAccountForm = memo<NewXeroBankAccountFormProps>((props) => {
    const { isLoading, selectedBankAccount, onSubmit, onClose } = props;

    const xeroReconnectWarningVisible = useAtomValue(xeroReconnectWarningVisibleState);

    const form = useForm<FormState>();

    const handleSubmit = form.handleSubmit((values) => onSubmit(values));

    useEffect(() => {
        if (selectedBankAccount) {
            // while selecting bank accounts, set defaults
            form.setValue('name', selectedBankAccount.name);
            form.setValue('currencyCode', selectedBankAccount.currencyCode);
            form.setValue('sortCode', selectedBankAccount.sortCode ?? '');
            form.setValue('bankAccountNumber', selectedBankAccount.bankAccountNumber ?? '');
        }
    }, [form, selectedBankAccount]);

    return (
        <Form data-testid='new-xero-ba-form' form={form} onSubmit={handleSubmit}>
            <Box bordered shadow='small' radius='small' spacing='16 24'>
                <Grid gap={16}>
                    <Text font='headline' fontSize='xxsmall' fontWeight='medium'>
                        {messages.newXeroBankAccount}
                    </Text>

                    <Grid gap={8}>
                        <Flex wrap={false} spacing='16'>
                            <Flex grow={1}>
                                <TextField
                                    required
                                    name='name'
                                    label={messages.accountCustomName}
                                    maxLength={150}
                                    validate={validatorHelpers.requiredStringWithErrorMessage}
                                />
                            </Flex>

                            <Flex width={80} shrink={0}>
                                <TextField disabled name='currencyCode' label={messages.currency} />
                            </Flex>
                        </Flex>

                        <TextField
                            required
                            name='sortCode'
                            label={messages.sortCode}
                            maxLength={6}
                            transform={transform}
                            validate={validatorHelpers.requiredStringWithErrorMessage}
                        />

                        <TextField
                            required
                            name='bankAccountNumber'
                            label={messages.accountNumber}
                            maxLength={8}
                            transform={transform}
                            validate={validatorHelpers.requiredStringWithErrorMessage}
                        />

                        <Fade in={xeroReconnectWarningVisible}>
                            <XeroReconnectWarning />
                        </Fade>

                        <Flex wrap={false} justifyContent='space-between'>
                            <Flex shrink={0}>
                                <Button color='midnight40' onClick={onClose}>
                                    {messages.cancel}
                                </Button>
                            </Flex>

                            <Button block color='blue80' type='submit' progress={isLoading}>
                                {messages.create}
                            </Button>
                        </Flex>
                    </Grid>
                </Grid>
            </Box>
        </Form>
    );
});

NewXeroBankAccountForm.displayName = 'NewXeroBankAccountForm';
